import { debugTracking } from '$lib/debug';
import { isTrackingAllowed } from '$lib/global/consent/consentGate';
import { shouldTrack } from '$lib/global/tracking/helper';
import { getTrackingService } from '$lib/global/tracking/TrackingServiceInstance';

import { getQueue, SEND_TIMEOUT_MS, send } from './queue';

interface TrackingPayload {
    type?: string;
    event?: string;
    name?: string;
    traits?: Record<string, unknown>;
    properties?: Record<string, unknown>;
    [key: string]: unknown;
}

export const track = (data: TrackingPayload) => {
    return enqueue('/t', { ...data, type: 'track' });
};

export const page = (data: TrackingPayload) => {
    return enqueue('/p', { ...data, type: 'page' });
};

export const identify = (data: TrackingPayload) => {
    return enqueue('/i', { ...data, type: 'identify' });
};
/**
 * CAP-4545: whether the backend can attribute a v4 message at all.
 *
 * Metrics rejects an activity without these ids and lemon-worker drops one without a campaign
 * or company, so sending it only loses it downstream — as noise in the error alerts. An event
 * fired before the first funnel page is known (e.g. `Cookies Accepted` on a slow load or on
 * `/consent-policy`) carries an empty `context.perspective`. Only the delivery to our own
 * backend stops here; Segment still hands the event to the third-party integrations.
 */
const isAttributable = (message: TrackingPayload): boolean => {
    const perspective = (message.context as { perspective?: Record<string, unknown> } | undefined)
        ?.perspective;

    return Boolean(
        perspective?.perspectivePageId &&
            perspective.perspectiveCampaignId &&
            perspective.perspectiveCompanyId,
    );
};

export const enqueue = (path: string, message: TrackingPayload) => {
    if (!shouldTrack()) {
        return;
    }

    // v3 carries its ids in `properties`, not `context.perspective`. Anything else runs the
    // V4Strategy — including a funnel without a `trackingVersion` (see `bootstrap`).
    if (!getTrackingService().isV3() && !isAttributable(message)) {
        debugTracking('-> dropped unattributable event', message.type, message.event);

        return;
    }

    const url = `/ps-fnl-events/api/data${path}`;

    // The retry queue is localStorage-backed by design (@segment/localstorage-retry), and
    // merely constructing it writes there — `.queue`, `.ack`, `.inProgress` and the reclaim
    // keys all appear before anything has failed. A visitor who rejected the banner gets no
    // persistent storage at all, so their events are fire-and-forget: a failed delivery is
    // dropped rather than retried. Consented visitors keep the retry behaviour unchanged.
    const queue = isTrackingAllowed() ? getQueue() : null;

    send(
        message,
        url,
        (err: { status: string; responseText: string }) => {
            if (err) {
                const lastPart = url.split('/').reverse()[0];
                console.warn(`Direct Tracking Error: /${lastPart} - ${err.status}`, {
                    data: {
                        url,
                        message,
                        error: JSON.stringify(err),
                        status: err.status,
                        responseText: err.responseText,
                    },
                });

                // On failure, add to the queue for retry.
                queue?.addItem({
                    url,
                    message,
                });
            }
        },
        SEND_TIMEOUT_MS,
    );
};

export default {
    track,
    page,
    identify,
};
